import { useEffect, useRef, useState } from 'react'
import { Check, ChevronDown, LogOut, PanelLeft, UserCircle } from 'lucide-react'
import { Avatar } from '@/components/ui/Avatar'

/** The avatar button in the top bar: who you are, Account, and Sign out (not the loudest thing on the page). */
export function UserMenu({
  name,
  email,
  onAccount,
  onSignOut,
  drawerOption,
}: {
  name: string
  email: string
  onAccount: () => void
  onSignOut: () => void
  /** Phones only: lets the person switch between the tab bar and a hamburger drawer. */
  drawerOption?: { on: boolean; onChange: (on: boolean) => void }
}) {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        trigger.current?.focus()
      }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const item =
    'flex w-full min-h-[44px] items-center gap-3 px-4 py-2 text-left text-body text-ink hover:bg-neutral-tint'

  return (
    <div ref={box} className="relative no-print">
      <button
        ref={trigger}
        type="button"
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex min-h-[44px] items-center gap-1 rounded-full p-1 hover:bg-neutral-tint"
      >
        <Avatar name={name} />
        <ChevronDown size={14} className="hidden text-muted sm:block" aria-hidden />
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Account menu"
          className="absolute right-0 top-full z-50 mt-1 w-64 overflow-hidden rounded-card border border-line bg-surface py-1 shadow-lg"
        >
          <div className="border-b border-line px-4 py-3">
            <p className="truncate text-body font-semibold text-ink">{name}</p>
            <p className="truncate text-label text-muted">{email}</p>
          </div>
          <button
            role="menuitem"
            className={item}
            onClick={() => {
              setOpen(false)
              onAccount()
            }}
          >
            <UserCircle size={18} className="text-muted" aria-hidden /> Account
          </button>
          {drawerOption && (
            <button
              role="menuitemcheckbox"
              aria-checked={drawerOption.on}
              className={item}
              onClick={() => {
                setOpen(false)
                drawerOption.onChange(!drawerOption.on)
              }}
            >
              <PanelLeft size={18} className="text-muted" aria-hidden />
              <span className="flex-1">Menu drawer</span>
              {drawerOption.on && <Check size={16} className="text-brand" aria-hidden />}
            </button>
          )}
          <button
            role="menuitem"
            className={item}
            onClick={() => {
              setOpen(false)
              onSignOut()
            }}
          >
            <LogOut size={18} className="text-muted" aria-hidden /> Sign out
          </button>
        </div>
      )}
    </div>
  )
}
